import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { AppState, Entry, PublicList, WantedItem } from '../../shared/api';
import { ApiError, api, useAppState, useToggleWanted, useUploadCanImage } from '../api';
import { Wall } from './Wall';
import { Wanted } from './Wanted';
import { Database } from './Database';
import { Overig } from './Overig';
import { AddSheet, DetailSheet, type AddDraft } from './EntrySheet';
import { LinkBarcodeSheet, NewFlavorSheet } from './CatalogSheets';
import { Toevoegen } from './Toevoegen';
import { FriendView } from './FriendView';
import { Toast, type ToastAction } from '../components/Toast';

/** The database has no tab of its own: it opens from Overig. */
type Tab = 'collectie' | 'wanted' | 'overig' | 'toevoegen' | 'database';
const TABS: Array<[Tab, string]> = [
  ['collectie', 'Collectie'],
  ['wanted', 'Wanted'],
  ['overig', 'Overig'],
];
const PAGES: Tab[] = ['collectie', 'wanted', 'overig', 'toevoegen', 'database'];

const receiveNote = (friend: string) => `Van ${friend}. Kies land en inhoud, dan komt hij in je collectie.`;

function useSessionState<T extends string>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      return (sessionStorage.getItem(key) as T) ?? initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  }, [key, value]);
  return [value, setValue] as const;
}

export function OwnerApp() {
  const navigate = useNavigate();
  const query = useAppState();

  useEffect(() => {
    if (query.error instanceof ApiError && query.error.status === 401) navigate('/login', { replace: true });
  }, [query.error, navigate]);

  if (query.data) return <Shell state={query.data} />;
  if (query.error && !(query.error instanceof ApiError && query.error.status === 401)) {
    return (
      <div className="center">
        <div className="login">
          <h1 className="login__title">
            MONSTERS
          </h1>
          <p className="card__text">De verbinding lukt niet. {query.error.message}</p>
          <button type="button" className="btn" style={{ marginTop: 18 }} onClick={() => query.refetch()}>
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }
  return <div className="splash">MONSTERS</div>;
}

function Shell({ state }: { state: AppState }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [storedTab, setTab] = useSessionState<Tab>('monsters.tab', 'collectie');
  // A session may still hold a tab that no longer exists.
  const tab = PAGES.includes(storedTab) ? storedTab : 'collectie';
  const [detailId, setDetailId] = useState<number | null>(null);
  const [draft, setDraft] = useState<AddDraft | null>(null);
  const [linkEan, setLinkEan] = useState<{ ean: string; suggestion: string | null } | null>(null);
  const [newFlavor, setNewFlavor] = useState<{ ean?: string; target: 'wall' | 'wanted' } | null>(null);
  const [preview, setPreview] = useState(false);
  const [toast, setToast] = useState<{ message: string; action?: ToastAction } | null>(null);
  const toastTimer = useRef(0);
  const uploadCanImage = useUploadCanImage();
  const toggleWanted = useToggleWanted();

  // A toast with an action stays a little longer, so there is time to tap it.
  const showToast = (message: string, action?: ToastAction) => {
    setToast({ message, action });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), action ? 6000 : 3200);
  };

  // Keep the last entry so the sheet can animate out after a delete.
  const lastDetail = useRef<Entry | null>(null);
  const detail = detailId === null ? null : (state.entries.find((e) => e.id === detailId) ?? lastDetail.current);
  if (detail) lastDetail.current = detail;

  const ownedCount = (canId: number) => state.entries.filter((e) => e.canId === canId).length;

  const startAdd = (canId: number, extra: Partial<AddDraft> = {}) => {
    setDetailId(null);
    setDraft({ canId, country: state.profile.defaultCountry, sizeMl: state.profile.defaultSizeMl, ...extra });
  };

  const scanNote = (canId: number) => {
    const n = ownedCount(canId);
    if (n) return `Al ${n}× in je collectie. Kies een ander land of inhoud om een nieuwe variant toe te voegen.`;
    return state.wanted.some((w) => w.canId === canId) ? 'Staat nu op je zoeklijst.' : undefined;
  };

  const wantedToast = (canId: number, wanted: boolean, previous?: WantedItem) => {
    const name = state.cans.find((c) => c.id === canId)?.name ?? 'Blik';
    if (wanted) showToast(`${name} staat op je zoeklijst.`);
    else showToast(`${name} is van je zoeklijst.`, {
      label: 'Ongedaan maken',
      onClick: () => setWanted(canId, true, previous),
    });
  };

  const setWanted = (canId: number, wanted: boolean, preference?: WantedItem) => {
    const previous = state.wanted.find((w) => w.canId === canId);
    toggleWanted.mutate(
      { canId, wanted, preference },
      {
        onSuccess: () => wantedToast(canId, wanted, previous),
        onError: (err) => showToast(err.message),
      },
    );
  };

  const logout = async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    qc.clear();
    navigate('/login', { replace: true });
  };

  const navTab = ([id, label]: (typeof TABS)[number]) => (
    <button
      key={id}
      type="button"
      className="nav__tab"
      aria-current={tab === id || (id === 'overig' && tab === 'database') ? 'page' : undefined}
      onClick={() => setTab(id)}
    >
      {label}
    </button>
  );

  return (
    <div className="app">
      {tab === 'collectie' && <Wall state={state} onOpen={setDetailId} />}
      {tab === 'wanted' && (
        <Wanted
          state={state}
          onAdd={({ canId, country, sizeMl }) => {
            const friend = state.reservations.find((r) => r.canId === canId)?.friendName;
            startAdd(canId, {
              ...(country ? { country } : {}),
              ...(sizeMl ? { sizeMl } : {}),
              ...(friend ? { note: receiveNote(friend) } : {}),
            });
          }}
          onWanted={setWanted}
          onRemove={async (want) => {
            await toggleWanted.mutateAsync({ canId: want.canId, wanted: false });
            wantedToast(want.canId, false, want);
          }}
          onNewFlavor={() => setNewFlavor({ target: 'wanted' })}
          onUploadImage={(id, file) =>
            uploadCanImage.mutate(
              { id, file },
              { onSuccess: () => showToast('Foto opgeslagen.'), onError: (err) => showToast(err.message) },
            )
          }
          onPreview={() => setPreview(true)}
          onReceive={(canId, friend) => startAdd(canId, { note: receiveNote(friend) })}
        />
      )}

      {tab === 'toevoegen' && (
        <Toevoegen
          state={state}
          onPick={(canId, barcode) =>
            startAdd(canId, {
              ...(barcode
                ? {
                    ean: barcode.ean,
                    scanned: true,
                    ...(barcode.country ? { country: barcode.country } : {}),
                    ...(barcode.sizeMl ? { sizeMl: barcode.sizeMl } : {}),
                  }
                : {}),
              note: scanNote(canId),
            })
          }
          onLink={(ean, suggestion) => setLinkEan({ ean, suggestion })}
          onNewFlavor={(ean) => setNewFlavor({ ean, target: 'wall' })}
        />
      )}
      {tab === 'overig' && (
        <Overig
          state={state}
          onDatabase={() => setTab('database')}
          onPreview={() => setPreview(true)}
          onLogout={logout}
          onToast={(message) => showToast(message)}
        />
      )}
      {tab === 'database' && (
        <Database
          state={state}
          onAddToWall={(canId) => startAdd(canId)}
          onWanted={setWanted}
          onNewFlavor={() => setNewFlavor({ target: 'wanted' })}
          onError={(message) => showToast(message)}
          onBack={() => setTab('overig')}
        />
      )}

      <nav className="nav" aria-label="Hoofdmenu">
        {TABS.map(navTab)}
        <button
          type="button"
          className="nav__add"
          aria-label="Toevoegen"
          aria-current={tab === 'toevoegen' ? 'page' : undefined}
          onClick={() => setTab('toevoegen')}
        >
          <span aria-hidden="true">+</span>
          <span className="nav__add-label" aria-hidden="true">
            Toevoegen
          </span>
        </button>
      </nav>

      {preview && <OwnerPreview slug={state.profile.shareSlug} onClose={() => setPreview(false)} />}

      {detail && detailId !== null && (
        <DetailSheet
          key={detail.id}
          state={state}
          entry={detail}
          onClose={() => setDetailId(null)}
          onError={(message) => showToast(message)}
        />
      )}
      {draft && (
        <AddSheet
          key={`${draft.canId}-${draft.ean ?? ''}`}
          state={state}
          draft={draft}
          onClose={() => setDraft(null)}
          onError={(message) => showToast(message)}
          onAdded={() => setTab('collectie')}
        />
      )}
      {linkEan && (
        <LinkBarcodeSheet
          state={state}
          ean={linkEan.ean}
          suggestion={linkEan.suggestion}
          onClose={() => setLinkEan(null)}
          onPick={(canId) => startAdd(canId, { ean: linkEan.ean, scanned: true, note: scanNote(canId) })}
          onNewFlavor={() => setNewFlavor({ ean: linkEan.ean, target: 'wall' })}
        />
      )}
      {newFlavor && (
        <NewFlavorSheet
          state={state}
          ean={newFlavor.ean}
          target={newFlavor.target}
          onClose={() => setNewFlavor(null)}
          onCreated={(can) => {
            if (newFlavor.target === 'wall') {
              startAdd(can.id, {
                ...(newFlavor.ean ? { ean: newFlavor.ean, scanned: true } : {}),
                note: scanNote(can.id),
              });
            } else {
              setTab('wanted');
              showToast(`${can.name} staat op je zoeklijst.`);
            }
          }}
        />
      )}

      {toast && <Toast message={toast.message} action={toast.action} />}
    </div>
  );
}

function OwnerPreview({ slug, onClose }: { slug: string; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['public-preview', slug],
    queryFn: () => api<PublicList>(`/api/public/${slug}`),
  });
  if (!query.data) return <div className="friend" />;
  return <FriendView list={query.data} preview={{ onClose }} />;
}
